import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  editEmailConfig,
  editRest,
  editSmpt,
  getEmailConfig,
  getRestConfig,
  getSmtp,
} from "../../../services/settings";
import { getUser, updateTenant } from "../../../services/user";
import { getAxiosResponseError } from "../../../utils/functions";

export const EMAIL_QUERY_KEYS = {
  smtp: ["settings", "smtp"],
  rest: ["settings", "rest"],
  emailConfigs: ["settings", "email-configs"],
  user: ["current-user"],
};

const queryOptions = {
  refetchOnWindowFocus: false,
  retry: 1,
};

export const useSmtpQuery = () =>
  useQuery({
    queryKey: EMAIL_QUERY_KEYS.smtp,
    queryFn: getSmtp,
    ...queryOptions,
  });

export const useRestConfigQuery = () =>
  useQuery({
    queryKey: EMAIL_QUERY_KEYS.rest,
    queryFn: getRestConfig,
    ...queryOptions,
  });

export const useEmailConfigsQuery = () =>
  useQuery({
    queryKey: EMAIL_QUERY_KEYS.emailConfigs,
    queryFn: getEmailConfig,
    ...queryOptions,
  });

export const useCurrentUserQuery = () =>
  useQuery({
    queryKey: EMAIL_QUERY_KEYS.user,
    queryFn: getUser,
    ...queryOptions,
  });

const showError = (err) =>
  toast.error(getAxiosResponseError(err) || "Something went wrong.");

export const useSaveEmailConfigMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ smtp, rest, mailSender }) => {
      if (smtp) await editSmpt(smtp);
      if (rest) await editRest(rest);
      if (mailSender) await updateTenant({ mailSender });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEYS.smtp });
      queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEYS.rest });
      queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEYS.user });
      toast.success("Changes saved.");
    },
    onError: showError,
  });
};

export const useSaveRoleNotificationsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (changedConfigs) => {
      await Promise.all(changedConfigs.map((c) => editEmailConfig(c)));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EMAIL_QUERY_KEYS.emailConfigs });
      toast.success("Changes saved.");
    },
    onError: showError,
  });
};
